import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Table,
  PageHeader,
  Button,
  Space,
  Input,
} from 'antd';
import {
  WifiOutlined,
  DownloadOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import {
  handleApiSuccess,
  handleApiError,
  MyBreadcrumb,
  accessControls,
} from 'utils';
import { wifiusersSuperAdminSearchService } from 'services/wifiusers';
import { branchcustomerListByIdService } from 'services/branchcustomer';
import Papa from 'papaparse';
import { debounce } from 'lodash';

const { Search } = Input;

export default function BranchnetworkStatsWifiusers() {
  const history = useHistory();
  const { branchCustomerId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [dataSource, setDataSource] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [branchCustomer, setBranchCustomer] = useState(null);

  const isSuperAdmin = accessControls.can(permissions, 'SuperAdmin.read:any');

  useEffect(() => {
    loadPermissions();
    loadBranchCustomer();
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchCustomerId]);

  const loadPermissions = () => {
    accessControls.fetch().then((permissions) => {
      setPermissions(permissions);
    });
  };

  const loadBranchCustomer = () => {
    if (branchCustomerId) {
      branchcustomerListByIdService(branchCustomerId)
        .then((response) => {
          if (response.data.items && response.data.items.length > 0) {
            setBranchCustomer(response.data.items[0]);
          }
        })
        .catch((error) => {
          handleApiError(error);
        });
    }
  };

  const loadData = useCallback((q, paginationPayload) => {
    setIsLoading(true);
    // Build query parameters including branchCustomerId
    let queryParams = {};
    if (paginationPayload) {
      queryParams = { ...paginationPayload };
    }
    if (branchCustomerId) {
      queryParams.branchCustomerId = branchCustomerId;
    }
    
    wifiusersSuperAdminSearchService(q || '', queryParams)
      .then((response) => {
        setIsLoading(false);
        if (response.data.result) {
          setDataSource(response.data.result);
        } else {
          setDataSource({ docs: [], totalDocs: 0 });
        }
      })
      .catch((error) => {
        setIsLoading(false);
        handleApiError(error);
      });
  });

  const searchRecord = (q) => {
    setIsLoading(true);
    setSearchQuery(q);
    loadData(q);
  };

  const exportWifiusersToCSV = () => {
    if (!dataSource || !dataSource.docs || dataSource.docs.length === 0) {
      handleApiError({ message: 'No Wi-Fi users to export' });
      return;
    }

    const exportData = dataSource.docs.map((user) => {
      let branchName = '';
      if (user.branchcustomer?.customername) {
        branchName = user.branchcustomer.customername;
      } else if (user.wifigroup?.branchcustomer?.customername) {
        branchName = user.wifigroup.branchcustomer.customername;
      } else if (user.branchnetwork_name) {
        branchName = user.branchnetwork_name;
      } else if (user.company?.company_name && user.branch?.branch_name) {
        branchName = `${user.company.company_name} - ${user.branch.branch_name}`;
      }

      return {
        Name: user.fullname || user.username || '',
        Email: user.username || '', // Using username as email if email field doesn't exist
        Branch: branchName,
        Passwords: user.password || '',
      };
    });

    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `wifi-users-${branchCustomer?.customername || 'all'}-${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'fullname',
      key: 'fullname',
      render: (text, record) => text || record.username || '-',
      sorter: (a, b) => {
        const aName = a.fullname || a.username || '';
        const bName = b.fullname || b.username || '';
        return aName.localeCompare(bName);
      },
    },
    {
      title: 'Email/Username',
      dataIndex: 'username',
      key: 'username',
      sorter: (a, b) => {
        if (!a.username || !b.username) return 0;
        return a.username.localeCompare(b.username);
      },
    },
    {
      title: 'Branch',
      key: 'branch',
      render: (text, record) => {
        // Try branchcustomer.customername first
        if (record.branchcustomer?.customername) {
          return record.branchcustomer.customername;
        }
        // Try wifigroup.branchcustomer if available
        if (record.wifigroup?.branchcustomer?.customername) {
          return record.wifigroup.branchcustomer.customername;
        }
        // Try branchnetwork_name
        if (record.branchnetwork_name) {
          return record.branchnetwork_name;
        }
        // Try company and branch
        if (record.company?.company_name && record.branch?.branch_name) {
          return `${record.company.company_name} - ${record.branch.branch_name}`;
        }
        return '-';
      },
    },
    {
      title: 'Password',
      dataIndex: 'password',
      key: 'password',
      render: (text) => text || '-',
    },
  ];

  const delayedQuery = debounce((q) => searchRecord(q), 500);
  const onChange = (e) => {
    delayedQuery(e.target.value);
  };

  return (
    <div>
      {accessControls.can(permissions, 'Client.read:any') && (
        <>
          <MyBreadcrumb
            items={[
              { label: 'Home', path: '/' },
              { label: 'Super Admin', path: '/super-admin' },
              {
                label: 'Branch Network Stats',
                path: '/branchnetwork/stats',
              },
              {
                label: branchCustomer?.customername
                  ? `Wi-Fi Users - ${branchCustomer.customername}`
                  : 'Wi-Fi Users',
                path: '',
              },
            ]}
          />
          <PageHeader
            ghost={false}
            avatar={{ icon: <WifiOutlined /> }}
            title={
              branchCustomer?.customername
                ? `Wi-Fi Users - ${branchCustomer.customername}`
                : 'Wi-Fi Users'
            }
            subTitle={
              <Search
                allowClear={true}
                onChange={onChange}
                loading={isLoading}
                placeholder="Filter search result"
                onSearch={(value) => searchRecord(value)}
                style={{ width: 300 }}
                defaultValue={searchQuery}
              />
            }
            extra={[
              <Button
                key="back"
                icon={<ArrowLeftOutlined />}
                onClick={() => history.push('/branchnetwork/stats')}
              >
                Back to Stats
              </Button>,
              isSuperAdmin && (
                <Button
                  key="export"
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={exportWifiusersToCSV}
                >
                  Export Wi-Fi Users
                </Button>
              ),
            ].filter(Boolean)}
          />
          <Table
            dataSource={dataSource?.docs || []}
            columns={columns}
            loading={isLoading}
            rowKey="_id"
            pagination={{
              total: dataSource?.totalDocs || 0,
              current: dataSource?.page || 1,
              pageSize: dataSource?.limit || 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
              onChange: (page, pageSize) => {
                const paginationPayload = {
                  page: page,
                  limit: pageSize,
                };
                loadData(searchQuery, paginationPayload);
              },
            }}
          />
        </>
      )}
    </div>
  );
}

